import { lazy, Suspense } from "react";
import NoAccountView from "src/apps/shared/NoAccountView";
import { Loading } from "src/components";
import { useAccountViewPreference } from "src/hooks/useAccountViewPreference";
import { lazyImport } from "src/lib";

const BrokerDashboardRoutes = lazy(() =>
  lazyImport(() =>
    import("src/apps/BrokerDashboard/routes").then((m) => ({ default: m.BrokerDashboardRoutes }))
  )
);
const ConveyancerDashboardRoutes = lazy(() =>
  lazyImport(() =>
    import("src/apps/ConveyancerDashboard/routes").then((m) => ({ default: m.ConveyancerDashboardRoutes }))
  )
);
const LOSRoutes = lazy(() =>
  lazyImport(() => import("src/apps/LOS/routes").then((m) => ({ default: m.LOSRoutes })))
);
const PortfolioDashboardRoutes = lazy(() =>
  lazyImport(() =>
    import("src/apps/PortfolioDashboard/routes").then((m) => ({ default: m.PortfolioDashboardRoutes }))
  )
);
const PostFundedDashboard = lazy(() =>
  lazyImport(() =>
    import("src/apps/PostFundedDashboard/routes").then((m) => ({ default: m.PostFundedDashboard }))
  )
);
const PreFundedDashboard = lazy(() =>
  lazyImport(() =>
    import("src/apps/PreFundedDashboard/routes").then((m) => ({ default: m.PreFundedDashboard }))
  )
);
const LenderDashboardRoutes = lazy(() =>
  lazyImport(() => import("./LenderDashboard/routes").then((m) => ({ default: m.LenderDashboardRoutes })))
);

export const BrokerOrBorrowerRoutes = () => {
  const { preference, isLoading } = useAccountViewPreference();

  if (isLoading && !preference) {
    return <Loading name="BrokerOrBorrowerRoutes - isLoading && !preference" />;
  }

  let routes: React.ReactNode = null;
  if (preference === "broker") {
    routes = <BrokerDashboardRoutes />;
  } else if (preference === "applicant") {
    routes = <PreFundedDashboard />;
  } else if (preference === "borrower") {
    routes = <PostFundedDashboard />;
  } else if (preference === "employee") {
    routes = <LOSRoutes />;
  } else if (preference === "desk-agent") {
    // Desk-agents share the employee shell but with capability-restricted views.
    // The LOSRoutes component reads `useIsDeskAgent()` to hide employee-only nav.
    routes = <LOSRoutes />;
  } else if (preference === "servicing") {
    routes = <PortfolioDashboardRoutes />;
  } else if (preference === "conveyancer") {
    routes = <ConveyancerDashboardRoutes />;
  } else if (preference === "lender") {
    routes = <LenderDashboardRoutes />;
  }

  if (!routes) {
    // Still settling (preference not yet resolved) → keep showing the spinner.
    // Settled with no matching dashboard (preference "none") → show a real
    // fallback instead of spinning forever.
    if (isLoading) {
      return <Loading name={`BrokerOrBorrowerRoutes - fallback. pref: ${preference}`} />;
    }
    return <NoAccountView />;
  }

  return <Suspense fallback={<Loading name="BrokerOrBorrowerRoutes - Suspense" />}>{routes}</Suspense>;
};
